import { useEffect } from "react";
import { EMAIL, Logo } from "../lib/site";
import { Footer } from "../components/Sections3";

const TITULAR = "José María Fernández Kherraz";
const NIF = "76753858Y";
const DOMICILIO = "Calle San Andrés 4, 29002 Málaga, España";
const TELEFONO = "+34 614 354 669";

function LegalShell({ title, testid, children }) {
  useEffect(() => {
    document.title = `${title} · Kanvia`;
    window.scrollTo(0, 0);
  }, [title]);
  return (
    <div className="min-h-screen bg-noche text-crema">
      <header className="border-b border-salvia/15">
        <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 md:px-8">
          <a href="/" className="flex items-center gap-3" data-testid="legal-logo-link">
            <Logo className="h-7 w-7" />
            <span className="wordmark font-display text-[21px] display-track font-semibold">
              Kanvia<span className="serif-accent text-salvia" aria-hidden="true">.</span>
            </span>
          </a>
          <a
            href="/"
            className="font-mono text-[11px] uppercase tracking-[0.2em] opacity-75 transition-opacity duration-300 hover:opacity-100"
            data-testid="legal-back-link"
          >
            ← Volver al inicio
          </a>
        </div>
      </header>
      <main className="mx-auto max-w-7xl px-5 py-16 md:px-8 md:py-24" data-testid={testid}>
        <p className="mono-tag">Kanvia · Documento legal</p>
        <h1 className="mt-6 max-w-[68ch] font-display display-track text-3xl leading-[1.05] sm:text-4xl lg:text-5xl">
          {title}
        </h1>
        <p className="mt-5 font-mono text-[11px] uppercase tracking-[0.2em] text-crema/70">
          Última actualización: 1 de septiembre de 2026
        </p>
        <div className="mt-12 max-w-[68ch]">{children}</div>
      </main>
      <Footer />
    </div>
  );
}

function B({ n, t, children }) {
  return (
    <section className="mt-12 border-t border-salvia/25 pt-6 first:mt-0">
      <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-crema/70">{n}</p>
      <h2 className="mt-3 font-display display-track text-xl">{t}</h2>
      <div className="mt-4 space-y-4 text-sm leading-relaxed text-crema/80">{children}</div>
    </section>
  );
}

export function AvisoLegal() {
  return (
    <LegalShell title="Aviso legal" testid="page-aviso-legal">
      <B n="01" t="Identificación del titular">
        <p>
          En cumplimiento del artículo 10 de la Ley 34/2002, de 11 de julio,
          de Servicios de la Sociedad de la Información y de Comercio
          Electrónico (LSSI-CE), se informa de que el titular de este sitio
          web es:
        </p>
        <p>
          Titular: {TITULAR}
          <br />
          NIF: {NIF}
          <br />
          Domicilio: {DOMICILIO}
          <br />
          Correo electrónico: {EMAIL}
          <br />
          Teléfono: {TELEFONO}
          <br />
          Nombre comercial: Kanvia
          <br />
          Dominio: kanvia.eu
        </p>
      </B>
      <B n="02" t="Objeto">
        <p>
          Este sitio web tiene carácter informativo: presenta el servicio de
          agentes de voz con inteligencia artificial de Kanvia para la
          atención telefónica y la toma de pedidos de restaurantes, y permite
          solicitar una demostración a través de un formulario de contacto.
        </p>
      </B>
      <B n="03" t="Condiciones de uso">
        <p>
          El acceso a esta web atribuye la condición de usuario e implica la
          aceptación de estas condiciones. El usuario se compromete a hacer un
          uso diligente del sitio, conforme a la ley y a la buena fe, y a no
          emplearlo para fines ilícitos, lesivos de derechos de terceros o que
          puedan dañar, inutilizar o sobrecargar la web o impedir su normal
          utilización.
        </p>
      </B>
      <B n="04" t="Propiedad intelectual e industrial">
        <p>
          Los contenidos de este sitio (textos, diseño, código, logotipo y
          marca «Kanvia») son titularidad de {TITULAR} o de terceros que han
          autorizado su uso, y están protegidos por la normativa de propiedad
          intelectual e industrial. Queda prohibida su reproducción,
          distribución o transformación sin autorización expresa. Las
          fotografías de banco de imágenes se utilizan conforme a las
          licencias de sus respectivos autores.
        </p>
      </B>
      <B n="05" t="Exclusión de responsabilidad y enlaces a terceros">
        <p>
          {TITULAR} no garantiza la disponibilidad ininterrumpida del sitio ni
          la ausencia de errores en los contenidos, aunque pondrá los medios
          razonables para corregirlos en cuanto los conozca. Los enlaces a
          servicios de terceros (por ejemplo, WhatsApp) se rigen por las
          condiciones de dichos terceros, sobre los que el titular no tiene
          control.
        </p>
      </B>
      <B n="06" t="Protección de datos y cookies">
        <p>
          El tratamiento de los datos personales que se realiza a través de
          este sitio y del servicio se describe en la{" "}
          <a href="/politica-de-privacidad" className="underline underline-offset-4">
            política de privacidad
          </a>
          . La información sobre las cookies y tecnologías similares está
          disponible en la{" "}
          <a href="/politica-de-cookies" className="underline underline-offset-4">
            política de cookies
          </a>
          .
        </p>
      </B>
      <B n="07" t="Legislación aplicable y jurisdicción">
        <p>
          Estas condiciones se rigen por la legislación española. Si el
          usuario actúa como consumidor, cualquier controversia se someterá a
          los juzgados y tribunales del domicilio del usuario. En los demás
          casos, las partes se someten a los juzgados y tribunales de Málaga.
        </p>
      </B>
      <B n="08" t="Uso de inteligencia artificial">
        <p>
          El servicio que presta el titular consiste en un asistente de voz
          automatizado basado en inteligencia artificial que atiende llamadas
          telefónicas. Conforme al artículo 50 del Reglamento (UE) 2024/1689
          de Inteligencia Artificial, se informa a las personas que llaman, al
          inicio de la llamada, de que están interactuando con un sistema
          automatizado, y pueden solicitar en cualquier momento ser atendidas
          por una persona.
        </p>
      </B>
    </LegalShell>
  );
}

const encargados = [
  {
    proveedor: "Emergent AI, Inc.",
    paraQue: "Alojamiento de la web y de la base de datos del formulario",
    donde: "EE. UU. — Cláusulas Contractuales Tipo",
  },
  {
    proveedor: "PostHog",
    paraQue: "Analítica de uso de la web, solo si el visitante la acepta",
    donde: "Cargada desde ap.emergent.sh",
  },
  {
    proveedor: "Vapi Labs, Inc.",
    paraQue: "Plataforma del asistente de voz: telefonía, transcripción y grabación",
    donde: "EE. UU. — Cláusulas Contractuales Tipo",
  },
  {
    proveedor: "Integromat s.r.o. (Make)",
    paraQue: "Automatización del envío de los pedidos al restaurante",
    donde: "Unión Europea",
  },
  {
    proveedor: "Sendinblue SAS (Brevo)",
    paraQue: "Envío de los correos con los pedidos",
    donde: "Unión Europea",
  },
];

export function PoliticaPrivacidad() {
  return (
    <LegalShell title="Política de privacidad" testid="page-privacidad">
      <B n="01" t="Responsable del tratamiento">
        <p>
          Responsable: {TITULAR} (Kanvia)
          <br />
          NIF: {NIF}
          <br />
          Domicilio: {DOMICILIO}
          <br />
          Correo de privacidad y de contacto: {EMAIL}
          <br />
          Teléfono: {TELEFONO}
        </p>
      </B>
      <B n="02" t="Qué datos recogemos">
        <p>
          A través del formulario de contacto recogemos únicamente los datos
          que tú introduces: nombre del local, ciudad, teléfono de contacto,
          volumen aproximado de llamadas diarias y, si lo escribes, un mensaje
          libre sobre cómo os entran los pedidos.
        </p>
        <p>
          Esta web no recoge más datos personales por otras vías. Si nos
          escribes por WhatsApp o correo electrónico, el tratamiento de esos
          canales se rige además por las políticas de sus proveedores.
        </p>
      </B>
      <B n="03" t="Para qué los usamos">
        <p>
          Finalidad única: atender tu consulta y ponernos en contacto contigo.
          No usamos tus datos para enviarte comunicaciones comerciales no
          solicitadas, no elaboramos perfiles y no tomamos decisiones
          automatizadas con efectos jurídicos sobre ti.
        </p>
      </B>
      <B n="04" t="Los dos tratamientos de esta empresa">
        <p>Kanvia trata datos personales en dos contextos distintos y con papeles distintos.</p>
        <p>
          a) Cuando tú contactas con nosotros. Los datos que envías por el
          formulario, por WhatsApp o por correo. Aquí Kanvia es el responsable
          del tratamiento y se aplica todo lo descrito en el resto de esta
          política.
        </p>
        <p>
          b) Cuando prestamos el servicio a un restaurante. El asistente de
          voz atiende las llamadas de los clientes de ese restaurante. Aquí el
          restaurante es el responsable del tratamiento y Kanvia actúa como
          encargado del tratamiento, en los términos del contrato de encargo
          firmado con cada cliente conforme al artículo 28 del RGPD. Kanvia no
          decide para qué se usan esos datos: los trata siguiendo las
          instrucciones del restaurante.
        </p>
      </B>
      <B n="05" t="Base jurídica">
        <p>
          La base jurídica del tratamiento es tu consentimiento (artículo
          6.1.a del RGPD), que prestas al marcar la casilla de aceptación de
          esta política antes de enviar el formulario. Puedes retirarlo en
          cualquier momento sin que ello afecte a la licitud del tratamiento
          anterior.
        </p>
      </B>
      <B n="06" t="Plazo de conservación">
        <p>
          Conservaremos tus datos durante el tiempo necesario para atender tu
          solicitud y, como máximo, durante 24 meses desde el último contacto.
          Después se suprimirán, salvo que exista una relación contractual o
          una obligación legal que exija conservarlos más tiempo.
        </p>
      </B>
      <B n="07" t="Destinatarios y encargados del tratamiento">
        <p>
          No cedemos tus datos a terceros, salvo obligación legal. El
          procesamiento del formulario corre a cargo del mismo alojamiento de
          la web: los envíos se almacenan en la base de datos del propio
          proyecto. Estos son los proveedores que tratan datos por cuenta de
          Kanvia o del restaurante:
        </p>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[560px] border-collapse text-sm" data-testid="tabla-encargados">
            <thead>
              <tr className="text-left font-mono text-[11px] uppercase tracking-[0.2em] text-crema/70">
                <th scope="col" className="border-b border-salvia/40 py-3 pr-4 font-medium">Proveedor</th>
                <th scope="col" className="border-b border-salvia/40 py-3 pr-4 font-medium">Para qué</th>
                <th scope="col" className="border-b border-salvia/40 py-3 font-medium">Dónde</th>
              </tr>
            </thead>
            <tbody>
              {encargados.map((e) => (
                <tr key={e.proveedor} className="align-top">
                  <td className="border-b border-salvia/20 py-3 pr-4 font-display display-track">{e.proveedor}</td>
                  <td className="border-b border-salvia/20 py-3 pr-4 text-crema/80">{e.paraQue}</td>
                  <td className="border-b border-salvia/20 py-3 text-crema/80">{e.donde}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p>
          Con cada encargado del tratamiento existen los contratos de encargo
          exigidos por el artículo 28 del RGPD.
        </p>
      </B>
      <B n="08" t="Transferencias internacionales">
        <p>
          Emergent AI, Inc. y Vapi Labs, Inc. tratan datos en Estados Unidos;
          esas transferencias se amparan en las Cláusulas Contractuales Tipo
          aprobadas por la Comisión Europea. El resto de proveedores trata los
          datos en la Unión Europea.
        </p>
      </B>
      <B n="09" t="Tus derechos">
        <p>
          Puedes ejercer en cualquier momento tus derechos de acceso,
          rectificación, supresión, oposición, limitación del tratamiento y
          portabilidad de los datos, así como retirar tu consentimiento.
        </p>
        <p>
          Para ejercerlos, escribe a {EMAIL} o a {DOMICILIO}, indicando el
          derecho que ejerces y acompañando información que permita verificar
          tu identidad. Responderemos en el plazo máximo de un mes.
        </p>
      </B>
      <B n="10" t="Reclamación ante la autoridad de control">
        <p>
          Si consideras que el tratamiento no se ajusta a la normativa, puedes
          presentar una reclamación ante la Agencia Española de Protección de
          Datos (C/ Jorge Juan 6, 28001 Madrid — www.aepd.es).
        </p>
      </B>
      <B n="11" t="Tratamiento de las llamadas atendidas por el asistente de voz">
        <p>
          <strong>Qué se trata.</strong> Grabación de la llamada, transcripción
          del audio, nombre de quien llama, número de teléfono, dirección de
          entrega, contenido del pedido y fecha y hora.
        </p>
        <p>
          <strong>Para qué.</strong> Generar el pedido por escrito y
          entregárselo al restaurante, permitir que el restaurante revise una
          llamada concreta si hay una incidencia, y comprobar el correcto
          funcionamiento del asistente.
        </p>
        <p>
          <strong>Base jurídica.</strong> La ejecución del pedido solicitado
          por el propio cliente (artículo 6.1.b del RGPD) y el interés
          legítimo del restaurante en poder revisar una llamada ante una
          reclamación (artículo 6.1.f).
        </p>
        <p>
          <strong>Información previa.</strong> Al inicio de cada llamada se
          informa de que se está hablando con un sistema automático y de que
          la llamada se graba. Quien llama puede solicitar en cualquier
          momento que se le transfiera a una persona.
        </p>
        <p>
          <strong>Plazo de conservación.</strong> Las grabaciones y
          transcripciones se conservan un máximo de 90 días y después se
          eliminan, salvo que sea necesario conservarlas para atender una
          reclamación concreta.
        </p>
        <p>
          <strong>Datos sobre alergias e intolerancias.</strong> Si durante la
          llamada el cliente menciona una alergia o una intolerancia
          alimentaria, esa información se recoge únicamente como una
          indicación del pedido dirigida a la cocina, se transmite al
          restaurante junto con el resto de la comanda y se elimina con ella.
          No se conserva asociada a la persona más allá del pedido, no se usa
          para elaborar ningún perfil y no se trata para ninguna otra
          finalidad.
        </p>
        <p>
          <strong>Obligación del restaurante.</strong> El restaurante, como
          responsable del tratamiento, debe informar a sus clientes de la
          existencia de la grabación y de la finalidad del tratamiento, y
          atender los derechos que estos ejerzan. Kanvia le presta la
          asistencia necesaria para ello.
        </p>
      </B>
      <B n="12" t="Cookies">
        <p>
          La información sobre las cookies y tecnologías similares que usa
          esta web está disponible en la{" "}
          <a href="/politica-de-cookies" className="underline underline-offset-4">
            política de cookies
          </a>
          .
        </p>
      </B>
    </LegalShell>
  );
}

export function PoliticaCookies() {
  return (
    <LegalShell title="Política de cookies" testid="page-cookies">
      <B n="01" t="Qué son las cookies">
        <p>
          Las cookies y tecnologías similares (como el almacenamiento local
          del navegador) son pequeños archivos o registros que un sitio web
          guarda en tu dispositivo para recordar información entre visitas.
        </p>
      </B>
      <B n="02" t="Qué usa exactamente esta web">
        <p>
          Hemos auditado técnicamente el sitio y esto es todo lo que carga.
          Esta web <strong>no usa cookies de marketing ni de publicidad</strong>.
        </p>
        <p>
          <strong>Técnicas necesarias (propias).</strong> «kanvia-consent-v1»,
          almacenamiento local del navegador: guarda la elección de
          consentimiento que haces en el banner de cookies. Duración: hasta
          que la borres de tu navegador. No requiere consentimiento por ser
          estrictamente necesaria.
        </p>
        <p>
          <strong>Analítica (de terceros, solo con tu consentimiento).</strong>{" "}
          PostHog: cookies y almacenamiento local con prefijo «ph_» que
          generan un identificador aleatorio para medir de forma agregada el
          uso de la web (páginas vistas y sesiones). Duración: hasta 12
          meses. El script se carga desde el dominio ap.emergent.sh únicamente
          después de que aceptes esta categoría: si la rechazas, no se realiza
          ninguna conexión con PostHog ni se instala ninguna de sus cookies.
        </p>
        <p>
          <strong>Recursos de terceros sin cookies.</strong> Las tipografías se
          descargan de Google Fonts y las fotografías de banco desde los
          servidores de sus proveedores; al cargar la página tu navegador se
          conecta a esos servidores, pero no instalan cookies en este sitio.
        </p>
        <p>
          Si dejamos de utilizar alguna de estas herramientas, esta política
          se actualizará antes de que el cambio afecte a los visitantes.
        </p>
      </B>
      <B n="03" t="Cómo cambiar o retirar tu consentimiento">
        <p>
          En el pie de todas las páginas encontrarás el enlace «Configurar
          cookies», que reabre el panel para aceptar, rechazar o ajustar cada
          categoría en cualquier momento. También puedes borrar las cookies y
          el almacenamiento local desde la configuración de tu navegador.
        </p>
      </B>
      <B n="04" t="Actualizaciones">
        <p>
          Esta política se revisará si cambian las tecnologías que usa la
          web. La fecha de la última actualización figura en la cabecera de
          esta página.
        </p>
      </B>
    </LegalShell>
  );
}
